"use client";

import { Box, Flex, Heading, Link, Stack, Text } from "@chakra-ui/react";
import NextLink from "next/link";

import { Reveal } from "@components/common/Reveal";
import { WHATSAPP_URL } from "@features/home/howItWorksGuide";
import { SiteFooter } from "@features/home/SiteFooter";
import { SiteTopBar } from "@features/home/SiteTopBar";
import { MINIMUM_BILLABLE_CBM } from "@features/services/shipping/rates";

const LAST_UPDATED = "12 July 2026";

interface TermsSection {
  title: string;
  paragraphs: string[];
}

const SECTIONS: TermsSection[] = [
  {
    title: "1. Who we are",
    paragraphs: [
      "Elk Cargo provides freight forwarding, consolidation, and sourcing-support services between China and Ghana, including receiving goods at our partner warehouses in China, sea and air freight, customs clearance coordination, and final-mile delivery within Ghana.",
      "By requesting a shipping label, delivering goods to our warehouse, or otherwise using our services, you agree to these terms.",
    ],
  },
  {
    title: "2. Quotes and rate classes",
    paragraphs: [
      "Freight is priced per cubic metre (CBM), and the rate depends on the class of goods — Standard, Electronics & Fragile, Special Care, or Restricted. The rate shown when you describe your goods is an estimate based on your description; the applicable class is confirmed when our warehouse receives and inspects the actual goods.",
      "If a shipment contains goods from more than one class, the entire shipment is billed at the highest class present. Certain goods — including e-bikes, motorbikes, scooters and tricycles — cannot be priced per CBM and are quoted per piece.",
      "Misdescribing goods does not lock in a lower rate: billing always follows what was actually shipped.",
    ],
  },
  {
    title: "3. Measurement and billing",
    paragraphs: [
      `Your final bill is based on the volume our warehouse measures when your cargo arrives, multiplied by your rate class, subject to a minimum billable volume of ${MINIMUM_BILLABLE_CBM} CBM. Estimates shown before measurement — including any figure derived from your own packing list — are indicative only.`,
      "Bills are issued in US dollars with a Ghana cedi equivalent shown at our prevailing exchange rate. The measured figures and the resulting bill appear on your tracking page before your container departs.",
    ],
  },
  {
    title: "4. Payment",
    paragraphs: [
      "Confirmed (measured) bills are payable in Ghana cedis. Payments made by MTN Mobile Money or Alipay must be confirmed with our team on WhatsApp, quoting your tracking number and the transaction reference.",
      "Keep your payment reference. In the event of any disagreement about whether a payment was made, the transaction reference is the record we verify against.",
      "Goods may be held until the bill for a shipment is settled in full.",
    ],
  },
  {
    title: "5. Prohibited and restricted goods",
    paragraphs: [
      "You must not ship illegal goods, counterfeit items, currency, firearms, ammunition, explosives, or any item prohibited under Chinese export law or Ghanaian import law. We may refuse, hold, or surrender to authorities any such goods found in a shipment, and you remain responsible for any resulting costs, fines, or penalties.",
      "Restricted items such as raw batteries, lithium cells, and paint carry a higher rate class and additional handling requirements. Declare them accurately — undeclared restricted goods endanger the whole container and may be removed from your shipment.",
    ],
  },
  {
    title: "6. Transit times",
    paragraphs: [
      "Typical transit is 35–50 days by sea and 5–10 days by air, measured from container departure. These are estimates, not guarantees: port congestion, customs inspection, vessel schedules, and other events beyond our control can extend them. We keep your tracking page and notifications current as status changes.",
    ],
  },
  {
    title: "7. Delivery",
    paragraphs: [
      "Final-mile delivery in Ghana uses the area, landmark directions, and GPS pin you provide with your shipping label. Our delivery team calls the phone number on file before heading out — keep it reachable. If a delivery attempt fails because the address information provided was inaccurate or the phone number was unreachable, a redelivery fee may apply.",
      "Delivery is complete when the goods are handed over at the address provided, to you or to a person present who accepts them on your behalf.",
    ],
  },
  {
    title: "8. Liability and insurance",
    paragraphs: [
      "We handle every shipment with care, but freight carries inherent risk. Unless separately agreed in writing, our liability for loss or damage to goods is limited to the freight charges paid for the affected shipment. We are not liable for indirect losses such as lost profit or lost business.",
      "Cargo insurance covering the value of your goods can be arranged on request, case by case, before your container departs — message us on WhatsApp if you want cover.",
      "Report visible loss or damage within 48 hours of delivery, with photos and your tracking number, so we can investigate while evidence is fresh.",
    ],
  },
  {
    title: "9. Uncollected goods",
    paragraphs: [
      "Goods that arrive in Ghana and remain unpaid or uncollected for an extended period may incur storage charges. Goods left unclaimed for more than 90 days after arrival, despite reasonable attempts to reach you, may be disposed of or sold to recover charges owed.",
    ],
  },
  {
    title: "10. Changes to these terms",
    paragraphs: [
      "We may update these terms as the service evolves. The version on this page, with its last-updated date, is the one that applies. Material changes take effect for shipments booked after the change is published.",
    ],
  },
];

export const TermsPage = () => (
  <Box bg="surface.background" minH="100dvh">
    <SiteTopBar />

    <Box as="section" px={{ base: 5, md: 10 }} py={{ base: 12, md: 16 }}>
      <Box maxW="3xl" mx="auto">
        <Reveal>
          <Stack gap={3}>
            <Text
              alignSelf="flex-start"
              fontSize="xs"
              fontFamily="mono"
              textTransform="uppercase"
              letterSpacing="wider"
              color="text.secondary"
              border="1px solid"
              borderColor="border.light"
              borderRadius="full"
              px={3}
              py={1}
            >
              Terms of use
            </Text>
            <Heading
              as="h1"
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight={700}
              letterSpacing=".02em"
            >
              The terms we ship by
            </Heading>
            <Text fontSize="sm" color="text.secondary">
              Last updated {LAST_UPDATED}. Plain language on purpose — if
              anything is unclear,{" "}
              <Link
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                textDecoration="underline"
                _hover={{ color: "fg" }}
              >
                ask us on WhatsApp
              </Link>{" "}
              before you ship. Current rates and billing rules live on the{" "}
              <Link
                as={NextLink}
                href="/how-it-works#rates"
                textDecoration="underline"
                _hover={{ color: "fg" }}
              >
                How it works
              </Link>{" "}
              page.
            </Text>
          </Stack>
        </Reveal>

        <Stack gap={{ base: 8, md: 10 }} mt={{ base: 10, md: 14 }}>
          {SECTIONS.map((section, i) => (
            <Reveal key={section.title} delay={Math.min(i * 40, 200)}>
              <Stack
                gap={3}
                pt={6}
                borderTopWidth="1px"
                borderColor="border.light"
              >
                <Heading as="h2" fontSize="lg" fontWeight={600}>
                  {section.title}
                </Heading>
                {section.paragraphs.map((paragraph) => (
                  <Text
                    key={paragraph.slice(0, 40)}
                    fontSize="sm"
                    color="text.secondary"
                    lineHeight={1.8}
                  >
                    {paragraph}
                  </Text>
                ))}
              </Stack>
            </Reveal>
          ))}

          <Reveal delay={200}>
            <Flex
              direction="column"
              gap={2}
              pt={6}
              borderTopWidth="1px"
              borderColor="border.light"
            >
              <Heading as="h2" fontSize="lg" fontWeight={600}>
                11. Contact
              </Heading>
              <Text fontSize="sm" color="text.secondary" lineHeight={1.8}>
                Questions about these terms, a bill, or a shipment:{" "}
                <Link
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  textDecoration="underline"
                  _hover={{ color: "fg" }}
                >
                  message us on WhatsApp
                </Link>{" "}
                with your tracking number.
              </Text>
            </Flex>
          </Reveal>
        </Stack>
      </Box>
    </Box>

    <SiteFooter />
  </Box>
);
